import React, { useState } from 'react';
import { Users, ClipboardCheck, Stethoscope } from 'lucide-react';
import { WorkersPage } from './WorkersPage';
import { InductionsPage } from './InductionsPage';
import { HealthMonitoringPage } from './HealthMonitoringPage';

type Tab = 'workers' | 'inductions' | 'health';

const TABS: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }>; description: string }[] = [
  { id: 'workers',    label: 'Workers',                        icon: Users,           description: 'Manage worker profiles, licences, certifications, drivers licences, and employment details for all staff.' },
  { id: 'inductions', label: 'Inductions',                     icon: ClipboardCheck,  description: 'Track and manage site induction records. View which workers are inducted on each site and generate induction PDFs.' },
  { id: 'health',     label: 'Health Monitoring & Fit Testing', icon: Stethoscope,     description: 'Monitor health surveillance and respiratory fit testing schedules. Highlights workers who are overdue for assessment.' },
];

interface WorkforcePageProps {
  defaultTab?: Tab;
}

export const WorkforcePage: React.FC<WorkforcePageProps> = ({ defaultTab = 'workers' }) => {
  const [active, setActive] = useState<Tab>(defaultTab);

  return (
    <div className="space-y-0">
      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 -mx-6 px-6 mb-6">
        <nav className="flex gap-1" aria-label="Workforce sections">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = active === tab.id;
            return (
              <div key={tab.id} className="relative group">
                <button
                  onClick={() => setActive(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 hidden group-hover:block">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl w-56 leading-snug text-center">
                    {tab.description}
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full border-4 border-transparent border-b-gray-900" />
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Page panels — kept mounted so state is preserved when switching tabs */}
      <div className={active === 'workers'    ? '' : 'hidden'}><WorkersPage /></div>
      <div className={active === 'inductions' ? '' : 'hidden'}><InductionsPage /></div>
      <div className={active === 'health'     ? '' : 'hidden'}><HealthMonitoringPage /></div>
    </div>
  );
};
